/**
 * Firm-wide document-coverage API (onboarding P2-3, spec §17 "missing-docs
 * visibility, firm-wide"). Backs /admin/document-coverage.
 *
 *   useFirmDocumentCoverage() → GET /v1/firm/document-coverage
 *     For the caller's tenant: every trust, the required core document set, what
 *     is on file vs missing per trust, and a book-wide summary (per required doc
 *     type, how many trusts are missing it). Tenant-wide-reader gated on the
 *     backend (admin / compliance / director / super_admin).
 *
 * The hook surfaces `isLoading`/`isError` so the screen renders skeletons rather
 * than zeros when the endpoint is not yet wired on a given environment.
 */

import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from './client';

/** A book-wide summary row — one per required core document type. */
export interface CoverageSummaryRow {
  /** Requirement key, e.g. 'trust-deed', 'letter-of-authority', 'trustee-id'. */
  docType: string;
  /** Human label for the dashboard, e.g. 'Letter of Authority'. */
  label: string;
  /** How many of the firm's trusts are missing this document. */
  missingCount: number;
}

/** Per-trust coverage row. */
export interface TrustCoverageRow {
  trustId: string;
  /** Trust display name, or null when not yet known. */
  name: string | null;
  /** Trust IT / Master reference, or null. */
  itNumber: string | null;
  /** Required-doc keys that ARE on file (baseline order). */
  onFile: string[];
  /** Required-doc keys that are MISSING (baseline order). */
  missing: string[];
  /** Count of distinct documents on file for the trust. */
  documentCount: number;
  /** Satisfied requirements ÷ total, 0..1. */
  coverage: number;
  /** First-class tenancy — the owning CLIENT tenant this trust belongs to. */
  clientTenantId?: string;
  /** The owning client's display name (the firm-wide grouping label). */
  clientDisplayName?: string;
}

export interface FirmDocumentCoverageResponse {
  totalTrusts: number;
  /** Trusts with every required core doc on file. */
  fullyCoveredTrusts: number;
  /** Mean per-trust coverage across the book (1 for an empty book). */
  overallCoverage: number;
  /** The required-doc keys, baseline order. */
  requiredSet: string[];
  summary: CoverageSummaryRow[];
  trusts: TrustCoverageRow[];
  /** First-class tenancy — active client links in the firm's book (the "of M"
   *  denominator; distinguishes "no clients" from "clients with no trusts"). */
  totalClients: number;
  /** Clients actually aggregated this request (after cap + grant verification). */
  shownClients?: number;
  /** True when the book exceeded the fan-out cap and was truncated. */
  truncated?: boolean;
  correlationId: string;
}

/**
 * Fail fast on a 404/403 (endpoint not wired on this env, or caller is not a
 * tenant-wide reader) rather than retrying three times — mirrors the
 * admin-metrics retry policy.
 */
function failFastOnMissing(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError && (error.status === 404 || error.status === 403)) {
    return false;
  }
  return failureCount < 3;
}

/** Exported so the admin-dashboard prefetch fills the SAME cache entry this
 *  hook reads. Duplicating the literal is how a prefetch silently stops working. */
export const FIRM_DOCUMENT_COVERAGE_QUERY_KEY = ['firm', 'document-coverage'] as const;

export function fetchFirmDocumentCoverage(signal?: AbortSignal) {
  return api<FirmDocumentCoverageResponse>('/api/v1/firm/document-coverage', { signal });
}

export function useFirmDocumentCoverage(opts: { enabled?: boolean } = {}) {
  return useQuery<FirmDocumentCoverageResponse>({
    queryKey: FIRM_DOCUMENT_COVERAGE_QUERY_KEY,
    queryFn: ({ signal }) => fetchFirmDocumentCoverage(signal),
    enabled: opts.enabled ?? true,
    retry: failFastOnMissing,
  });
}
